import { ResponsiveTable } from '../components/ResponsiveTable';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  Plus,
  Trash,
  Truck,
  Loader2,
  Building2,
  Store,
  AlertCircle
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { TableScrollContainer } from '../components/TableScrollContainer';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useToast } from '../components/Toast';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { getApiUrl, apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { RootState } from '../store';

interface Employee {
  id: string;
  personId: string;
  companyId?: string | null;
  shopId?: string | null;
  type?: 'VENDOR' | 'SECRETARY' | 'OTHER' | null;
  notes?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  person?: {
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    email?: string | null;
    phone?: string | null;
  };
  company?: { id: string; name: string } | null;
  shop?: { id: string; name: string } | null;
}

interface EmployeesResponse {
  success: boolean;
  data: {
    employees: Employee[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message?: string;
}

export function EmployeesListPage() {
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const { user } = useSelector((state: RootState) => state.auth);
  const canHardDelete = user?.role === 'super_admin';

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState<{ isOpen: boolean; employeeIds: string[] }>({
    isOpen: false,
    employeeIds: [],
  });
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkHardDeleteConfirm, setBulkHardDeleteConfirm] = useState<{ isOpen: boolean; employeeIds: string[] }>({
    isOpen: false,
    employeeIds: [],
  });
  const [bulkHardDeleting, setBulkHardDeleting] = useState(false);

  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [shops, setShops] = useState<Array<{ id: string; name: string }>>([]);

  // Filters and pagination
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const limit = 20;

  const visibleEmployeeIds = useMemo(() => employees.map((employee) => employee.id), [employees]);
  const {
    selectedIds,
    selectedCount,
    selectedArray,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleEmployeeIds);
  const { needsShopSelection, canLoad, currentShopId, isAllShopsMode } = useShopScopedPage();

  useEffect(() => {
    if (currentShopId) {
      setShopFilter(currentShopId);
    }
  }, [currentShopId]);

  useEffect(() => {
    if (!canLoad) return;
    fetchEmployees();
  }, [canLoad, currentShopId, search, statusFilter, companyFilter, shopFilter, typeFilter, sortKey, sortDir, page]);

  useEffect(() => {
    loadCompanies();
    loadShops();
  }, []);

  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/companies?limit=200'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCompanies((data.data?.companies || []).map((c: any) => ({ id: c.id, name: c.name })));
        }
      }
    } catch (err) {
      console.error('Failed to load companies:', err);
    }
  };

  const loadShops = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/shops?limit=200'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShops((data.data?.shops || []).map((s: any) => ({ id: s.id, name: s.name })));
        }
      }
    } catch (err) {
      console.error('Failed to load shops:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortKey,
        sortDir,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(companyFilter && { companyId: companyFilter }),
        ...(shopFilter && { shopId: shopFilter }),
        ...(typeFilter && { type: typeFilter }),
      });

      const response = await apiClient(`/api/admin/employees?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch employees');
      }

      const data: EmployeesResponse = await response.json();

      if (data.success) {
        setEmployees(data.data.employees || []);
        setTotal(data.data.total);
        setTotalPages(data.data.totalPages);
      } else {
        setError(data.message || 'Failed to load employees');
      }
    } catch (err) {
      console.error('Fetch employees error:', err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleCreate = () => {
    navigate('/employees/create');
  };

  const handleEdit = (employeeId: string) => {
    navigate(`/employees/${employeeId}/edit`);
  };

  const handleDelete = async (employee: Employee) => {
    const name =
      employee.person?.displayName ||
      `${employee.person?.firstName || ''} ${employee.person?.lastName || ''}`.trim() ||
      'cet employé';
    const confirmed = window.confirm(`Supprimer ${name} ? Cette action est définitive.`);
    if (!confirmed) return;

    try {
      setDeletingId(employee.id);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/employees/${employee.id}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        const reason = data?.message || data?.error || 'Impossible de supprimer l’employé';
        setError(reason);
        showToast(reason, 'error', 7000);
        return;
      }

      showToast('Employé supprimé', 'success');
      setEmployees((prev) => prev.filter((item) => item.id !== employee.id));
      clearSelection();
    } catch (err) {
      console.error('Delete employee error:', err);
      setError('Impossible de supprimer l’employé');
      showToast('Impossible de supprimer l’employé', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const updateEmployeeStatus = async (employeeIds: string[], isActive: boolean) => {
    if (employeeIds.length === 0) return;
    setBulkUpdating(true);

    try {
      const token = localStorage.getItem('token');
      const failed: string[] = [];

      for (const employeeId of employeeIds) {
        const response = await fetch(getApiUrl(`/api/admin/employees/${employeeId}`), {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isActive }),
        });

        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          failed.push(employeeId);
        }
      }

      if (failed.length > 0) {
        showToast(`Échec de mise à jour pour ${failed.length} employé(s).`, 'error', 7000);
      } else {
        showToast(isActive ? 'Employés activés' : 'Employés désactivés', 'success');
      }

      await fetchEmployees();
      clearSelection();
    } catch (err) {
      console.error('Bulk update employee status error:', err);
      showToast('Impossible de mettre à jour les employés', 'error', 7000);
    } finally {
      setBulkUpdating(false);
    }
  };

  const openBulkDelete = () => {
    if (selectedArray.length === 0) return;
    setBulkDeleteConfirm({ isOpen: true, employeeIds: selectedArray });
  };

  const cancelBulkDelete = () => {
    setBulkDeleteConfirm({ isOpen: false, employeeIds: [] });
  };

  const openBulkHardDeleteConfirm = () => {
    if (!canHardDelete || selectedArray.length === 0) return;
    setBulkHardDeleteConfirm({ isOpen: true, employeeIds: selectedArray });
  };
  const cancelBulkHardDelete = () => setBulkHardDeleteConfirm({ isOpen: false, employeeIds: [] });
  const confirmBulkHardDelete = async () => {
    const ids = bulkHardDeleteConfirm.employeeIds;
    if (ids.length === 0) return;
    try {
      setBulkHardDeleting(true);
      const token = localStorage.getItem('token');
      const failed: string[] = [];
      for (const id of ids) {
        try {
          const resp = await fetch(getApiUrl(`/api/admin/employees/${id}?hardDelete=true`), {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (!resp.ok) failed.push(id);
        } catch {
          failed.push(id);
        }
      }
      setBulkHardDeleteConfirm({ isOpen: false, employeeIds: [] });
      clearSelection();
      await fetchEmployees();
      if (failed.length > 0) showToast(`${failed.length} employé(s) n'ont pas pu être supprimés définitivement`, 'error');
      else showToast(`${ids.length} employé(s) supprimé(s) définitivement`, 'success');
    } finally {
      setBulkHardDeleting(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (bulkDeleteConfirm.employeeIds.length === 0) return;
    setBulkUpdating(true);

    try {
      const token = localStorage.getItem('token');
      const failed: string[] = [];

      for (const employeeId of bulkDeleteConfirm.employeeIds) {
        const response = await fetch(getApiUrl(`/api/admin/employees/${employeeId}`), {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          failed.push(employeeId);
        }
      }

      const deletedCount = bulkDeleteConfirm.employeeIds.length - failed.length;
      if (deletedCount > 0) {
        showToast(`${deletedCount} employé(s) supprimé(s)`, 'success');
      }
      if (failed.length > 0) {
        showToast(`Échec de suppression pour ${failed.length} employé(s).`, 'error', 7000);
      }

      await fetchEmployees();
      clearSelection();
    } catch (err) {
      console.error('Bulk delete employee error:', err);
      showToast('Impossible de supprimer les employés', 'error', 7000);
    } finally {
      setBulkUpdating(false);
      cancelBulkDelete();
    }
  };

  const formatEmployeeName = (employee: Employee) => {
    return (
      employee.person?.displayName ||
      `${employee.person?.firstName || ''} ${employee.person?.lastName || ''}`.trim() ||
      'Employé'
    );
  };

  const formatDate = (value: string) => new Date(value).toLocaleDateString('fr-FR');
  const formatEmployeeType = (value?: Employee['type'] | null) => {
    switch (value) {
      case 'VENDOR':
        return 'Vendeur';
      case 'SECRETARY':
        return 'Secrétaire';
      default:
        return 'Autre';
    }
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="dt-list-toolbar flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Truck className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Employés</h1>
              <p className="text-sm text-gray-500">Gérez vos employés et leurs rattachements</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <HelpIcon docPath="employees" />
          <button
            onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouvel employé
          </button>
          {selectedCount > 0 && (
            <div className="ml-3 flex items-center space-x-2">
              <button
                onClick={() => updateEmployeeStatus(selectedArray, true)}
                disabled={bulkUpdating}
                className="px-4 py-2 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-50"
              >
                Activer
              </button>
              <button
                onClick={() => updateEmployeeStatus(selectedArray, false)}
                disabled={bulkUpdating}
                className="px-4 py-2 border border-yellow-300 text-yellow-700 rounded-lg hover:bg-yellow-50 disabled:opacity-50"
              >
                Désactiver
              </button>
              <button
                onClick={openBulkDelete}
                disabled={bulkUpdating || bulkHardDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center space-x-2"
              >
                <Trash className="w-4 h-4" />
                <span>Supprimer ({selectedCount})</span>
              </button>
              {canHardDelete && (
                <button
                  onClick={openBulkHardDeleteConfirm}
                  disabled={bulkUpdating || bulkHardDeleting}
                  className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 inline-flex items-center space-x-2"
                  title="Suppression définitive"
                >
                  <AlertCircle className="w-4 h-4" />
                  <span>Suppression définitive ({selectedCount})</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <ToastComponent />

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkHardDeleteConfirm.isOpen}
        onConfirm={confirmBulkHardDelete}
        onClose={cancelBulkHardDelete}
        entityLabel={bulkHardDeleteConfirm.employeeIds.length !== 1 ? 'employé(s)' : 'employé'}
        count={bulkHardDeleteConfirm.employeeIds.length}
        isLoading={bulkHardDeleting}
      />

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="dt-filters flex flex-nowrap items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-0 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher un employé..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-field pl-10 w-full"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field shrink-0 w-[160px]"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="input-field shrink-0 w-[180px]"
          >
            <option value="">Toutes les entreprises</option>
            {companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
          <select
            value={shopFilter}
            onChange={(e) => setShopFilter(e.target.value)}
            className="input-field shrink-0 w-[160px]"
          >
            <option value="">Tous les shops</option>
            {shops.map((shop) => (
              <option key={shop.id} value={shop.id}>
                {shop.name}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="input-field shrink-0 w-[140px]"
          >
            <option value="">Tous les types</option>
            <option value="VENDOR">Vendeur</option>
            <option value="SECRETARY">Secrétaire</option>
            <option value="OTHER">Autre</option>
          </select>
          <button
            onClick={() => { setSearch(''); setStatusFilter(''); setCompanyFilter(''); setShopFilter(''); setTypeFilter(''); setPage(1); }}
            className="btn-secondary flex items-center justify-center space-x-2 shrink-0"
          >
            <Filter className="w-4 h-4" />
            <span>Effacer</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <TableScrollContainer hint="Faites défiler →">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    ref={selectAllCheckboxRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employé</span>
                    {sortKey === 'name' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Téléphone
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entreprise
                </th>
                <th className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${isAllShopsMode ? '' : 'hidden'}`}>
                  Shop
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('type')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Type</span>
                    {sortKey === 'type' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('isActive')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Statut</span>
                    {sortKey === 'isActive' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Créé le</span>
                    {sortKey === 'createdAt' && (
                      <ChevronDown className={`w-4 h-4 transform ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Chargement...
                    </div>
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-gray-500">
                    Aucun employé trouvé
                  </td>
                </tr>
              ) : (
                employees.map((employee) => (
                  <tr key={employee.id} className="group hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(employee.id)}
                        onChange={() => toggleSelect(employee.id)}
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{formatEmployeeName(employee)}</div>
                      {employee.notes ? <div className="text-xs text-gray-500 line-clamp-1">{employee.notes}</div> : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{employee.person?.email || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{employee.person?.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{employee.company?.name || '-'}</td>
                    <td className={`px-4 py-3 text-sm text-gray-600 ${isAllShopsMode ? '' : 'hidden'}`}>{employee.shop?.name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatEmployeeType(employee.type)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        employee.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {employee.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(employee.createdAt)}</td>
                    <td className="px-4 py-3 text-right sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(employee.id)}
                          className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(employee)}
                          disabled={deletingId === employee.id}
                          className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          title="Supprimer"
                        >
                          {deletingId === employee.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </TableScrollContainer>

        {/* Pagination */}
        {!loading && employees.length > 0 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{(page - 1) * limit + 1}</span> à{' '}
                <span className="font-medium">{Math.min(page * limit, total)}</span> sur{' '}
                <span className="font-medium">{total}</span> employés
              </div>
              <div className="flex-1 flex justify-end">
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading overlay for subsequent loads */}
      {loading && employees.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 flex items-center space-x-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
            <span>Chargement...</span>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteConfirm.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer <strong>{bulkDeleteConfirm.employeeIds.length}</strong> employé
              {bulkDeleteConfirm.employeeIds.length !== 1 ? 's' : ''} ? Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                disabled={bulkUpdating}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={bulkUpdating}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {bulkUpdating ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
